import { Info } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpsertScreenshot } from '@/hooks/use-upsert-screenshot';
import { ApiError } from '@/lib/api-client';
import type { StorePlatform, StoreScreenshot } from '@/types/store';

import {
  PLATFORM_IMAGE_TYPES,
  PLATFORM_LABELS,
  PLATFORM_LOCALES,
  SELECT_CLASS,
} from './store-constants';

interface ScreenshotSlotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  platform: StorePlatform;
  /** Editing an existing slot: key fields (locale/type/position) are locked. */
  slot?: StoreScreenshot;
}

/**
 * Registers a screenshot slot by its R2 object key. The slot is keyed on
 * (platform, locale, imageType, position); editing an existing slot only
 * changes its object key. There is no file upload — the image must already be
 * in the R2 bucket.
 */
export function ScreenshotSlotDialog({
  open,
  onOpenChange,
  platform,
  slot,
}: ScreenshotSlotDialogProps): JSX.Element {
  const editing = Boolean(slot);
  const [locale, setLocale] = useState('');
  const [imageType, setImageType] = useState('');
  const [position, setPosition] = useState('0');
  const [objectKey, setObjectKey] = useState('');
  // Field-scoped so aria-invalid/aria-describedby only mark the offending input.
  const [error, setError] = useState<{
    field: 'position' | 'objectKey' | 'form';
    message: string;
  } | null>(null);
  const upsert = useUpsertScreenshot();

  useEffect(() => {
    if (!open) return;

    setLocale(slot?.locale ?? PLATFORM_LOCALES[platform][0]);
    setImageType(slot?.imageType ?? PLATFORM_IMAGE_TYPES[platform][0]);
    setPosition(slot ? String(slot.position) : '0');
    setObjectKey(slot?.objectKey ?? '');
    setError(null);
  }, [open, slot, platform]);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const positionNum = Number(position);

    if (!Number.isInteger(positionNum) || positionNum < 0) {
      setError({ field: 'position', message: 'Position must be a whole number ≥ 0.' });

      return;
    }
    if (!objectKey.trim()) {
      setError({ field: 'objectKey', message: 'An R2 object key is required.' });

      return;
    }

    upsert.mutate(
      {
        platform,
        locale,
        body: { imageType, position: positionNum, objectKey: objectKey.trim() },
      },
      {
        onSuccess: () => {
          toast.success(`Saved ${imageType} #${positionNum} (${locale}).`);
          onOpenChange(false);
        },
        onError: (err) => {
          setError({
            field: 'form',
            message: err instanceof ApiError ? err.message : 'Could not save the slot.',
          });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Edit' : 'Add'} {PLATFORM_LABELS[platform]} screenshot
          </DialogTitle>
          <DialogDescription>
            Register a screenshot by its R2 object key — there is no file upload here.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-border bg-secondary p-2.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
          <p>
            Upload the image to the R2 bucket first, then register its object key here. Publishing
            replaces every screenshot of this image type for the locale.{' '}
            {editing
              ? 'Locale, image type and position are fixed — only the object key is editable.'
              : 'Slots cannot be deleted, and locale, image type and position lock once saved — only the object key stays editable.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="slot-locale">Locale</Label>
              {editing ? (
                <Input id="slot-locale" value={locale} readOnly className="font-mono" />
              ) : (
                <select
                  id="slot-locale"
                  className={SELECT_CLASS}
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                >
                  {PLATFORM_LOCALES[platform].map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="slot-position">Position</Label>
              {editing ? (
                <Input id="slot-position" value={position} readOnly className="font-mono" />
              ) : (
                <Input
                  id="slot-position"
                  type="number"
                  min={0}
                  step={1}
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  className="font-mono"
                  aria-invalid={error?.field === 'position'}
                  aria-describedby={error?.field === 'position' ? 'slot-error' : undefined}
                />
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="slot-image-type">Image type</Label>
            {editing ? (
              <Input id="slot-image-type" value={imageType} readOnly className="font-mono" />
            ) : (
              <select
                id="slot-image-type"
                className={SELECT_CLASS}
                value={imageType}
                onChange={(e) => setImageType(e.target.value)}
              >
                {PLATFORM_IMAGE_TYPES[platform].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="slot-object-key">R2 object key</Label>
            <Input
              id="slot-object-key"
              value={objectKey}
              onChange={(e) => setObjectKey(e.target.value)}
              placeholder="store-screenshots/en-US/phoneScreenshots/1.png"
              className="font-mono"
              aria-invalid={error?.field === 'objectKey'}
              aria-describedby={error?.field === 'objectKey' ? 'slot-error' : undefined}
            />
          </div>

          {error && (
            <p id="slot-error" className="text-sm text-destructive" role="alert">
              {error.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save slot'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
